/**
 * <roll-platter-builder>
 * Distributes a fixed number of rolls (per the selected variant) across a roll list.
 * Enables the product form's submit button only when the distribution is valid,
 * and writes the selection into the `properties[Rolls]` line-item property.
 *
 * Depends on (optional): PUB_SUB_EVENTS / subscribe() from global.js for variant changes.
 * Falls back to listening on the form's hidden `[name="id"]` input if those are missing.
 *
 * Robust to the theme re-rendering parts of the product section on variant change:
 * the submit button and form are re-resolved on every validate, and in-progress
 * quantities are mirrored to sessionStorage so a DOM swap doesn't lose them.
 */
if (!customElements.get('roll-platter-builder')) {
  class RollPlatterBuilder extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      try {
        this.config = JSON.parse(this.querySelector('script.rpb-config').textContent);
      } catch (e) {
        console.warn('[roll-platter-builder] invalid config', e);
        return;
      }

      this.statusEl = this.querySelector('.rpb-status');
      this.propertyEl = this.querySelector('.rpb-property');
      this.propertyDataEl = this.querySelector('.rpb-property-data');
      this.rows = Array.from(this.querySelectorAll('.rpb-row'));
      this.list = this.querySelector(".rpb-list");
      this.storageKey = 'rpb:' + (this.dataset.productHandle || 'product');

      if (this.rows.length === 0 || !this.getForm() || !this.getVariantInput()) return;

      // One configured platter per add-to-cart: hide the quantity selector, force qty = 1.
      if (this.dataset.forceQuantityOne === 'true') this.lockQuantityToOne();

      this.bindEvents();
      this.restoreState();
      this.currentVariantId = String(this.getVariantInput().value || '');
      this.validate();
    }

    disconnectedCallback() {
      if (this.variantUnsub) this.variantUnsub();
      this.persistState();
    }

    /* ----- element lookups (re-resolved each time; the theme may re-render) - */

    getForm() {
      return document.getElementById(this.dataset.formId);
    }

    getSubmitBtn() {
      return document.getElementById(this.dataset.submitId);
    }

    getVariantInput() {
      const form = this.getForm();
      return form ? form.querySelector('[name="id"]') : null;
    }

    /* ----- wiring ------------------------------------------------------- */

    bindEvents() {
      // Steppers + manual entry (event delegation, scoped to this element).
      this.addEventListener('click', (e) => {
        const dec = e.target.closest('.rpb-dec');
        const inc = e.target.closest('.rpb-inc');
        if (!dec && !inc) return;
        const row = e.target.closest('.rpb-row');
        if (!row) return;
        this.setRowQty(row, this.getRowQty(row) + (inc ? 1 : -1));
      });

      this.addEventListener('input', (e) => {
        if (!e.target.classList.contains('rpb-qty')) return;
        const row = e.target.closest('.rpb-row');
        if (row) this.setRowQty(row, e.target.value, /* keepFocus */ true);
      });

      this.addEventListener('change', (e) => {
        if (!e.target.classList.contains('rpb-qty')) return;
        const row = e.target.closest('.rpb-row');
        if (row) this.setRowQty(row, e.target.value);
      });

      // Variant changes — prefer the theme's pub/sub, fall back to the hidden input.
      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS === 'object' &&
        PUB_SUB_EVENTS &&
        PUB_SUB_EVENTS.variantChange
      ) {
        this.variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
          const v = payload && payload.data ? payload.data.variant : null;
          const input = this.getVariantInput();
          this.onVariantChange(String(v && v.id ? v.id : input ? input.value : ''));
        });
      } else {
        this.bindVariantInputListener();
      }

      this.bindFormSubmitGuard();

      // bfcache / autofill restore.
      window.addEventListener('pageshow', () => this.validate());
    }

    bindVariantInputListener() {
      const input = this.getVariantInput();
      if (!input || input === this._boundVariantInput) return;
      this._boundVariantInput = input;
      input.addEventListener('change', () => this.onVariantChange(String(input.value)));
    }

    bindFormSubmitGuard() {
      const form = this.getForm();
      if (!form || form === this._boundForm) return;
      this._boundForm = form;
      form.addEventListener(
        'submit',
        (e) => {
          if (this.builderActive && !this.lastValid) {
            e.preventDefault();
            e.stopPropagation();
            if (this.statusEl) this.statusEl.focus({ preventScroll: false });
          }
        },
        true
      );
    }

    /* Show/hide the whole builder, not just the roll list.

       Only `.rpb-list` used to be hidden, so on a Chef's Choice variant the
       heading ("Select the quantity for each roll…") and any instructions were
       left on screen above a plain Add to Cart, telling the shopper to pick
       rolls that aren't there. */
    setBuilderVisible(visible) {
      const parts = [
        this.list,
        this.querySelector('.rpb-heading'),
        this.querySelector('.rpb-instructions'),
      ];
      parts.forEach((el) => {
        if (el) el.hidden = !visible;
      });
    }

    lockQuantityToOne() {
      const wrap = this.dataset.quantityId ? document.getElementById(this.dataset.quantityId) : null;
      if (wrap) wrap.hidden = true;
      const form = this.getForm();
      const qtyInput = form ? form.querySelector('[name="quantity"]') : null;
      if (qtyInput) qtyInput.value = 1;
    }

    /* ----- per-row qty helpers ----------------------------------------- */

    perRollMax() {
      const v = this.variantConfig();
      if (!v) return 0;
      if (this.config.allowMultiplePerRoll === false) return 1;
      // Default cap = the variant's largest target, so "all in one roll" is allowed.
      const ceiling = Math.max(Number(v.limit) || 0, Number(v.sigLimit) || 0);
      const cap = Number(v.maxPerRoll);
      return cap > 0 ? Math.min(cap, ceiling || cap) : ceiling;
    }

    getRowQty(row) {
      const n = Math.floor(Number(row.querySelector('.rpb-qty').value));
      return Number.isFinite(n) && n > 0 ? n : 0;
    }

    setRowQty(row, value, keepFocus) {
      const max = this.perRollMax();
      let n = Math.floor(Number(value));
      if (!Number.isFinite(n) || n < 0) n = 0;
      if (max > 0 && n > max) n = max;
      const input = row.querySelector('.rpb-qty');
      if (String(n) !== input.value) input.value = String(n);
      if (!keepFocus && document.activeElement === input) input.blur();
      this.validate();
    }

    /* ----- variant handling -------------------------------------------- */

    onVariantChange(id) {
      this.currentVariantId = String(id || '');
      // Re-clamp every row to the new variant's per-roll cap, then re-validate.
      const max = this.perRollMax();
      this.rows.forEach((row) => {
        if (max > 0 && this.getRowQty(row) > max) row.querySelector('.rpb-qty').value = String(max);
      });
      // The theme may have swapped the form/button DOM — re-attach guards.
      this.bindFormSubmitGuard();
      this.bindVariantInputListener();
      this.validate();
    }

    variantConfig() {
      return this.config.variants ? this.config.variants[this.currentVariantId] : null;
    }

    /* ----- core validation --------------------------------------------- */

    validate() {
      const v = this.variantConfig();
      const limit = v ? Number(v.limit) : NaN;
      const submitBtn = this.getSubmitBtn();

      /* No usable limit for this variant (e.g. a Chef's Choice style) → hide the
         builder and hand Add to Cart back to the theme. */
      if (!v || !Number.isFinite(limit) || limit <= 0) {
        this.builderActive = false;
        this.setBuilderVisible(false);
        if (this.statusEl) this.statusEl.textContent = '';
        this.setProperty('');
        this.rows.forEach((row) => {
          const dec = row.querySelector('.rpb-dec');
          const inc = row.querySelector('.rpb-inc');
          if (dec) dec.disabled = true;
          if (inc) inc.disabled = true;
        });

        /* Re-enable the submit button. This branch used to return without
           touching it, on the assumption it was still "as the theme set it" —
           but the builder itself disables it while a Build Your Own selection is
           incomplete. So picking Build Your Own, not finishing, then switching
           to Chef's Choice left Add to Cart permanently dead. Only an
           unavailable variant should block it here. */
        if (submitBtn) {
          const soldOut = v ? v.available === false : false;
          submitBtn.disabled = soldOut;
          submitBtn.setAttribute('aria-disabled', String(soldOut));
          submitBtn.removeAttribute('aria-describedby');
        }

        this.persistState();
        return;
      }

      this.builderActive = true;
      this.setBuilderVisible(true);

      const tiered = v.sigLimit != null && Number.isFinite(Number(v.sigLimit));
      const sigLimit = tiered ? Number(v.sigLimit) : 0;

      let sumRegular = 0;
      let sumSignature = 0;
      const picks = [];
      this.rows.forEach((row) => {
        const qty = this.getRowQty(row);
        const isSig = tiered && row.dataset.tier === 'signature';
        if (isSig) sumSignature += qty;
        else sumRegular += qty;
        if (qty > 0) {
          picks.push({ name: row.dataset.rollName, key: row.dataset.rollKey, tier: row.dataset.tier, qty: qty });
        }
      });

      // Sanity: is the target even reachable with the rolls available?
      const max = this.perRollMax();
      const sigRollCount = this.rows.filter((r) => r.dataset.tier === 'signature').length;
      const regRollCount = this.rows.length - (tiered ? sigRollCount : 0);
      const unreachable = regRollCount * max < limit || (tiered && sigRollCount * max < sigLimit);

      let valid = false;
      let message = '';
      let isError = false;

      if (unreachable) {
        valid = false;
        isError = true;
        message = this.config.strings.configError;
        console.warn('[roll-platter-builder] limit not reachable with the configured rolls', this.currentVariantId);
      } else if (tiered) {
        valid = sumRegular === limit && sumSignature === sigLimit;
        isError = sumRegular > limit || sumSignature > sigLimit;
        message = valid
          ? this.config.strings.complete
          : this.fill(this.config.strings.tierHint, {
              reg_count: sumRegular,
              reg_total: limit,
              sig_count: sumSignature,
              sig_total: sigLimit
            });
      } else {
        valid = sumRegular === limit;
        if (sumRegular > limit) {
          isError = true;
          message = this.fill(this.config.strings.overLimit, { total: limit });
        } else if (valid) {
          message = this.config.strings.complete;
        } else {
          message = this.fill(this.config.strings.countHint, {
            count: sumRegular,
            total: limit,
            remaining: limit - sumRegular
          });
        }
      }

      this.lastValid = valid;

      // Property string — human-readable; only non-zero rolls; stable row order.
      if (valid && picks.length) {
        this.setProperty(picks.map((p) => p.name + ' - ' + p.qty).join(', '), JSON.stringify(picks));
      } else {
        this.setProperty(''); // omit the property entirely while invalid
      }

      // Status line.
      if (this.statusEl) {
        this.statusEl.textContent = message;
        this.statusEl.dataset.state = isError ? 'error' : valid ? 'complete' : 'info';
      }

      // Submit button — never enable something the theme disabled (sold out, etc.).
      if (submitBtn) {
        const themeBlocks = v.available === false;
        const disabled = themeBlocks || !valid;
        submitBtn.disabled = disabled;
        submitBtn.setAttribute('aria-disabled', String(disabled));
        if (this.statusEl && this.statusEl.id) submitBtn.setAttribute('aria-describedby', this.statusEl.id);
      }

      this.refreshStepperButtons(sumRegular, sumSignature, tiered, limit, sigLimit);
      this.persistState();
    }

    refreshStepperButtons(sumRegular, sumSignature, tiered, regLimit, sigLimit) {
      const max = this.perRollMax();
      this.rows.forEach((row) => {
        const qty = this.getRowQty(row);
        const isSig = tiered && row.dataset.tier === 'signature';
        const bucketSum = isSig ? sumSignature : sumRegular;
        const bucketLimit = isSig ? sigLimit : regLimit;
        const dec = row.querySelector('.rpb-dec');
        const inc = row.querySelector('.rpb-inc');
        if (dec) dec.disabled = !this.builderActive || qty <= 0;
        if (inc) inc.disabled = !this.builderActive || (max > 0 && qty >= max) || bucketSum >= bucketLimit;
      });
    }

    /* ----- property + persistence -------------------------------------- */

    setProperty(text, dataJson) {
      if (this.propertyEl) this.propertyEl.value = text || '';
      if (this.propertyDataEl) this.propertyDataEl.value = text ? dataJson || '' : '';
    }

    persistState() {
      try {
        const state = {};
        this.rows.forEach((row) => {
          const q = this.getRowQty(row);
          if (q > 0) state[row.dataset.rollKey] = q;
        });
        sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch (e) {
        /* storage unavailable — ignore */
      }
    }

    restoreState() {
      try {
        const raw = sessionStorage.getItem(this.storageKey);
        if (!raw) return;
        const state = JSON.parse(raw);
        this.rows.forEach((row) => {
          const q = Number(state[row.dataset.rollKey]);
          if (Number.isFinite(q) && q > 0) row.querySelector('.rpb-qty').value = String(Math.floor(q));
        });
      } catch (e) {
        /* ignore */
      }
    }

    /* ----- tiny templating --------------------------------------------- */

    fill(tpl, vars) {
      return String(tpl == null ? '' : tpl).replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
    }
  }

  customElements.define('roll-platter-builder', RollPlatterBuilder);
}
